/**
 * What a search engine and a link preview see.
 *
 * ── The problem this solves ───────────────────────────────────────────────
 *
 * PROVIA is a single-page app: one `index.html`, one `<title>`, one meta
 * description, served for every route. So the pricing page, the privacy policy
 * and the product tour were all, as far as a crawler was concerned, a page
 * called "PROVIA — Property Management" about "Properties, tenants, rent
 * verification, bills and documents in one place." Whichever one ranked, it
 * ranked for the wrong words, and a link to any of them previewed identically
 * in a message.
 *
 * Every public route now declares its own. `scripts/prerender.mjs` then writes
 * each one out as real HTML at build time, so none of this depends on the
 * crawler running JavaScript.
 *
 * ── One list, three consumers ─────────────────────────────────────────────
 *
 * `PUBLIC_ROUTES` below is read by this module, by the sitemap generator and
 * by the prerenderer. A page added in one place is added in all three, which
 * is the only arrangement where a new page cannot quietly end up unlisted,
 * unrendered, or both.
 */

export const SITE = {
  /** Canonical origin. No trailing slash. */
  url: 'https://provia.uk',
  name: 'PROVIA',
  /** The company behind the product — they are not the same thing. */
  company: 'OPSERION',
  /**
   * Plain `en`, not `en-GB`.
   *
   * The vocabulary here is British — "letting agent", "tenancy", "deposit
   * protection" — and Australia reads all of it without blinking. But a
   * region tag is a targeting signal as well as a spelling one, and this
   * product is not for one country: 36_Jurisdictions.gs has the local rules
   * for ten, the currency is per organisation, and the second market is
   * Australia. Saying en-GB told a search engine to prefer serving these
   * pages in Britain, which is a strange thing to ask for when somebody in
   * Brisbane is looking for exactly this.
   *
   * One site, one language, no hreflang alternates — there is nothing to
   * alternate between until there is a page written for a country.
   */
  locale: 'en',
  /** 1200×630, the size every major preview crops to. */
  ogImage: '/og.png',
} as const;

export interface PageMeta {
  /** Without the site name — `pageTitle` appends it. */
  title: string;
  description: string;
  /** Path only, e.g. "/pricing". */
  path: string;
  /** Kept out of the sitemap and marked noindex. */
  noindex?: boolean;
  /** Extra JSON-LD for this page, beyond the site-wide graph. */
  jsonLd?: Record<string, unknown>[];
}

/**
 * Every page a stranger may arrive at, and the words it should rank for.
 *
 * The descriptions are written to be read in a result listing rather than to
 * hold keywords: the click comes from a sentence that answers the question the
 * search asked, and Google rewrites a stuffed one anyway.
 */
export const PUBLIC_ROUTES: PageMeta[] = [
  {
    path: '/',
    title: 'Property management software for landlords and letting agents',
    description:
      'Track properties and rooms, verify rent as it arrives, split utility bills, '
      + 'keep documents in one place and give tenants a portal of their own. '
      + 'Built for landlords and small agencies in the UK, Australia and beyond, '
      + 'with local wording for deposits, bonds and tenancies.',
  },
  {
    path: '/tour',
    title: 'See PROVIA — every screen',
    description:
      'Real screenshots of the running app: the dashboard, rent verification, the '
      + 'reminder queue, texting a tenant, and what tenants see in their own portal.',
  },
  {
    path: '/pricing',
    title: 'Pricing',
    description:
      'Simple monthly pricing by number of properties, with a free trial and no card '
      + 'required. Export your data at any time, on any plan.',
  },
  {
    path: '/terms',
    title: 'Terms of service',
    description: 'The terms on which PROVIA is provided.',
  },
  {
    path: '/privacy',
    title: 'Privacy policy',
    description:
      'What PROVIA stores about you and your tenants, where it is kept, and how to '
      + 'have it removed.',
  },
  {
    path: '/data-processing',
    title: 'Data processing agreement',
    description:
      'The data processing terms between OPSERION and landlords using PROVIA, for '
      + 'UK GDPR and equivalent data protection purposes.',
  },
  {
    path: '/refunds',
    title: 'Refunds and cancellation',
    description: 'How to cancel PROVIA, what happens to your data, and when refunds apply.',
  },
  {
    path: '/cookies',
    title: 'Cookies',
    description: 'What PROVIA stores in your browser, and what it is for.',
  },
];

/** The page meta for a path, or null when the path is not a public page. */
export function metaForPath(path: string): PageMeta | null {
  const clean = path.replace(/\/+$/, '') || '/';
  return PUBLIC_ROUTES.find((r) => r.path === clean) ?? null;
}

/**
 * The full title.
 *
 * The home page does not repeat the site name — its title already leads with
 * the product, and "PROVIA … | PROVIA" is the commonest own goal in this
 * whole subject.
 */
export function pageTitle(meta: PageMeta): string {
  if (meta.path === '/') return `${SITE.name} — ${meta.title}`;
  // A page whose title is already the site name — the fallback used for every
  // signed-in screen — must not become "PROVIA | PROVIA".
  if (meta.title === SITE.name) return SITE.name;
  return `${meta.title} | ${SITE.name}`;
}

export const canonicalFor = (path: string) =>
  `${SITE.url}${path === '/' ? '/' : path.replace(/\/+$/, '')}`;

/**
 * The site-wide structured data.
 *
 * Two linked things rather than one, because they are two things: OPSERION is
 * a company and PROVIA is a product it publishes. Collapsing them is how a
 * knowledge panel ends up attributing the software to nobody.
 *
 * `offers` carries the real starting price. A price in structured data that
 * disagrees with the pricing page is worse than none — it is the kind of
 * mismatch that gets rich results withdrawn — so it is generated from the same
 * figure the page shows.
 */
export function siteJsonLd(startingPrice?: { amount: string; currency: string }) {
  const organisation = {
    '@type': 'Organization',
    '@id': `${SITE.url}/#organisation`,
    name: SITE.company,
    url: SITE.url,
    logo: `${SITE.url}/favicon.svg`,
  };

  const product: Record<string, unknown> = {
    '@type': 'SoftwareApplication',
    '@id': `${SITE.url}/#software`,
    name: SITE.name,
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Property management software',
    operatingSystem: 'Web browser',
    url: SITE.url,
    publisher: { '@id': `${SITE.url}/#organisation` },
    description: PUBLIC_ROUTES[0].description,
    /*
     * Every country the product has local knowledge of, not every country it
     * would take money from.
     *
     * "Area served" is how a local search decides whether to show a result at
     * all, so the list is worth getting right in both directions: one entry
     * kept PROVIA out of results in Australia, and a list of everywhere would
     * be a claim we cannot support. These are the ten in
     * 36_Jurisdictions.gs — the ones where the app knows what a deposit is
     * called, who holds it and what the landlord is expected to do with it.
     */
    areaServed: [
      { '@type': 'Country', name: 'United Kingdom' },
      { '@type': 'Country', name: 'Australia' },
      { '@type': 'Country', name: 'New Zealand' },
      { '@type': 'Country', name: 'Ireland' },
      { '@type': 'Country', name: 'United States' },
      { '@type': 'Country', name: 'Canada' },
      { '@type': 'Country', name: 'South Africa' },
      { '@type': 'Country', name: 'Kenya' },
      { '@type': 'Country', name: 'Nigeria' },
      { '@type': 'Country', name: 'India' },
    ],
  };

  if (startingPrice) {
    product.offers = {
      '@type': 'Offer',
      price: startingPrice.amount,
      priceCurrency: startingPrice.currency,
      availability: 'https://schema.org/InStock',
      url: `${SITE.url}/pricing`,
    };
  }

  return [{ '@context': 'https://schema.org', '@graph': [organisation, product] }];
}
